import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { Plus, Kanban as KanbanIcon, StickyNote } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"; // Assuming i18n is needed
import { namespace } from '@common/config/constants'; // Assuming namespace

// Logic imports
import { Kanban as typeKanban } from "@common-models/kanban";
import { BlueticksForm } from '@common-models/form';
import { getSelectedBoardId, getCurrentBoard } from '@common-reducers/BoardsSelectors';
import { DBKanbanThunks, DBFormThunks } from "@common-reducers/DBServiceThunks";
import { taskActions } from '@common-reducers/TasksReducer';
import { selectUserId } from "@common-reducers/UserSelectors";
import { dynamicColumnListForForms } from '@common-reducers/TaskSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import randomstring from 'randomstring';


type Props = {
    /** The menu opened or closed — the "Add view" tooltip around it keeps itself shut while open. */
    onOpenChange?: (open: boolean) => void;
};

export default function AddViewDropdownShadcn({ onOpenChange }: Props = {}) {
    // Portal logic
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);
    
    // Redux & Logic hooks
    const dispatch = useAppDispatch();
    const selectedBoardId = useAppSelector(getSelectedBoardId);
    const userId = useAppSelector(selectUserId);
    const currentBoard = useAppSelector(getCurrentBoard);
    const columnList = useAppSelector(state => dynamicColumnListForForms(state, currentBoard?._id));
    const { t } = useTranslation(namespace);

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    // --- Handlers ---
    
    const handleCreateKanban = async () => {
        if (!selectedBoardId || !userId) return;
        const _kanban: typeKanban = {
            owner: userId ?? '',
            boardId: selectedBoardId,
            name: "Kanban",
        };
        dispatch(taskActions.setCurrentBoardView({
            type: "loader",
            id: '',
        }));
        const res = await dispatch(DBKanbanThunks.create(_kanban));
        const kanban = res.payload as typeKanban;
        if (kanban && kanban._id) {
             dispatch(taskActions.setCurrentBoardView({
                type: "kanban",
                id: kanban._id.toString(),
            }));
        }
    };

    const handleCreateForm = async () => {
        if (!currentBoard) return;
        
        let newColumnData = {};
        // Logic adapted from new-form-button.tsx
        for (let i = 0; i < columnList.length; i++) {
            const col = columnList[i];
            if (!col?._id) continue;
            const colId = col._id;
            if (colId === 'name') {
                newColumnData = {
                    ...newColumnData,
                    [colId]: {
                        order: -99,
                        visible: true,
                        title: t('newFormButton.newTaskTitle', "Task Name"), // Fallback if translation missing
                        description: t('newFormButton.newTaskDescription', "Description"),
                        required: false,
                    }
                };
            }
            else {
                newColumnData = {
                    ...newColumnData,
                    [colId]: {
                        order: i,
                        visible: true,
                        title: col.title === '' ? col.type : col.title,
                        required: false,
                    }
                };
            }
        }
        
        const uniqueId = randomstring.generate(24);
        const _form: BlueticksForm = {
            owner: userId,
            boardId: currentBoard._id,
            name: "Form",
            columnData: newColumnData,
            shareId: uniqueId,
            isActive: true,
            afterSubmit: {
                title: t('newFormButton.formAfterSubmitTitle', "Thank you!"),
                message: t('newFormButton.formAfterSubmitMessage', "Your response has been submitted."),
            },
            backgroundColor: 'var(--tasks-group-bg)',
        };

        dispatch(taskActions.setCurrentBoardView({
            type: "loader",
            id: '',
        }));
        const res = await dispatch(DBFormThunks.create(_form));
        const form = res.payload as BlueticksForm;
        if (form) {
            dispatch(taskActions.setCurrentBoardView({
                type: "form",
                id: form._id,
            }));
        }
    };

    return (
        <DropdownMenu onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <div 
                    className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-slate-200 cursor-pointer transition-colors"
                    data-testid='add-feature-plus-button-shadcn'
                >
                    <Plus className="h-5 w-5 text-gray-500" />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
                align="start" 
                className="w-48 z-[1400]"
                container={portalContainer || localPortalContainer}
            >
                <DropdownMenuItem onClick={handleCreateKanban} className="cursor-pointer">
                    <KanbanIcon className="mr-2 h-4 w-4" />
                    <span>Kanban</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={handleCreateForm} className="cursor-pointer">
                    <StickyNote className="mr-2 h-4 w-4" /> 
                    {/* Using StickyNote for Form as NoteAltOutlined substitute */}
                    <span>Form</span>
                </DropdownMenuItem>
                
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
